import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ReactComponent as AddIcon } from "../../../../../assets/images/add_plus_white.svg";
import AlertMsg from "../../../../../components/AlertMsg";
import Container from "../../../../../components/Container";
import Loader from "../../../../../components/Loader";
import CategoryMenuList from "../../../../../components/restaurant/CategoryMenuList";
import ConfirmAlert from "../../../../../components/restaurant/ConfirmAlert";
import SettingCategoryList from "../../../../../components/restaurant/SettingCategoryList";
import RestaurantHeader from "../../../../../components/RestaurantHeader";
import SearchInput from "../../../../../components/SearchInput";
import useDebounce from "../../../../../components/UseDebounce";
import { BTN_COLOR, BTN_TEXT_COLOR } from "../../../../../constants";
import { useGetCategoryList, useDeleteCategory } from "../../categories/services";
import { useGetMenuList } from "../services";

const AntTabs = styled(Tabs)({
  backgroundColor: "#FFF",
  borderRadius: 8,
  borderBottom: 0,
  height: 0,
  width: "fit-content",
  "& .MuiTabs-indicator": {
    height: 0,
  },
});

const AntTab = styled((props) => <Tab disableRipple {...props} />)(() => ({
  textTransform: "none",
  minWidth: 150,
  height: 40,
  fontFamily: "GilroyBold",
  color: "#00000099",
  "&.Mui-selected": {
    color: "#FFF",
    backgroundColor: "#000",
    borderRadius: 8,
  },
}));

const SettingsMenu = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const restaurantId = useParams().rest_id;

  const [tabValue, setTabValue] = useState(
    location.pathname.split("/").length >= 5
      ? location.pathname.split("/")[4]
      : "menu"
  );
  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });
  const [searchName, setSearchName] = useState("");
  const [searchItemName, setSearchItemName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [categoryItemData, setCategoryItemData] = useState(null);

  const debouncedCatName = useDebounce(searchName, 500);
  const debouncedMenuName = useDebounce(searchItemName, 500);

  const { data: allCategories = [], isLoading: categoriesLoading } = useGetCategoryList(restaurantId);
  const { data: allMenuItems = [] } = useGetMenuList(restaurantId);
  const deleteCategoryMutation = useDeleteCategory(restaurantId);

  const categoryList = useMemo(
    () => allCategories.filter((x) => x?.name?.toLowerCase().includes(debouncedCatName)),
    [allCategories, debouncedCatName]
  );

  const onCloseAlertMsg = () => {
    setMsgAlert({ open: false, message: "", msgType: "error" });
  };

  const onTabChange = (event, newValue) => {
    setTabValue(newValue === 0 ? "menu" : "category");
  };

  const onAddCatMenu = () => {
    if (tabValue === "category") {
      navigate("/restaurant/" + restaurantId + "/add_category");
    } else {
      navigate("/restaurant/" + restaurantId + "/add_menu");
    }
  };

  const editCategory = (data) => {
    navigate("/restaurant/" + restaurantId + "/add_category/" + data._id);
  };

  const deleteCategory = (data) => {
    const menuExist = allMenuItems.some((x) => x._idCategory === data._id);
    setCategoryItemData({ ...data, menuExist });
    setConfirmDelete(true);
  };

  const onCloseConfirmDelete = () => {
    setConfirmDelete(false);
    setCategoryItemData(null);
  };

  const confirmDeleteMenu = () => {
    deleteCategoryMutation.mutate(categoryItemData._id, {
      onSuccess: () => {
        setConfirmDelete(false);
        setCategoryItemData(null);
      },
      onError: () =>
        setMsgAlert({ open: true, message: "Failed to delete category", msgType: "error" }),
    });
  };

  if (categoriesLoading) {
    return (
      <Container page={"restaurants"} searchVal={""} onSearch={() => null} searchLabel="">
        <Loader height={{ height: window.innerHeight - 150 }} />
      </Container>
    );
  }

  return (
    <Container
      page={"restaurants"}
      searchVal={""}
      onSearch={() => null}
      searchLabel=""
    >
      <Grid mt={-2}>
        <Grid
          item
          container
          xs={12}
          style={{ display: "flex", alignItems: "center" }}
        >
          <Grid item xs={12}>
            <Box sx={styles.titleMainBox}>
              <RestaurantHeader
                title={"Upload Manually"}
                onBackBtnClick={() =>
                  navigate("/restaurant/" + restaurantId + "/manage_menu")
                }
              />
              <Box my={1} mr={2} sx={{ minWidth: 250 }}>
                <SearchInput
                  value={tabValue === "category" ? searchName : searchItemName}
                  setValue={(val) =>
                    tabValue === "category"
                      ? setSearchName(val)
                      : setSearchItemName(val)
                  }
                  placeholder={
                    tabValue === "category"
                      ? "Search category here"
                      : "Search menu item here"
                  }
                  inputBg={"#FFFFFF"}
                  height={48}
                />
              </Box>
              <Box my={1}>
                <AntTabs
                  value={tabValue === "category" ? 1 : 0}
                  onChange={onTabChange}
                >
                  <AntTab label="Menu Settings" />
                  <AntTab label="Category Settings" />
                </AntTabs>
              </Box>
              <Box
                sx={styles.addCategoryBtn}
                onClick={() => onAddCatMenu()}
                my={1}
              >
                <AddIcon width={18} height={18} />
                <Typography sx={styles.addCategoryText} ml={0.5}>
                  Add {tabValue === "category" ? "Category" : "Menu"}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box mb={3} mt={1} sx={{ minHeight: window.innerHeight - 350 }}>
          <Grid item xs={12} sx={styles.mainView} py={2} px={3}>
            {tabValue === "category" ? (
              <>
                <Grid
                  item
                  xs={12}
                  mb={1.5}
                  container
                  direction="row"
                  justifyContent={"flex-end"}
                >
                  <Box
                    sx={styles.editCatOrderDiv}
                    onClick={() =>
                      navigate(
                        "/restaurant/" + restaurantId + "/category_reorder"
                      )
                    }
                  >
                    <AddIcon width={18} height={18} />
                    <Typography sx={styles.editCatOrderText} ml={0.5}>
                      Edit Category Order
                    </Typography>
                  </Box>
                </Grid>
                <SettingCategoryList
                  categoryList={categoryList}
                  editCategory={editCategory}
                  deleteCategory={deleteCategory}
                />
              </>
            ) : (
              <CategoryMenuList
                page={"SettingsMenu"}
                restaurantId={restaurantId}
                searchMenu={debouncedMenuName}
                categoryList={allCategories}
                allowSort={true}
                showGridIcon={true}
                showMenuPopup={true}
              />
            )}
          </Grid>
        </Box>
      </Grid>

      <ConfirmAlert
        confirmDelete={confirmDelete}
        title={
          categoryItemData?.menuExist === true
            ? "Menu items exists for this category"
            : "Are you sure?"
        }
        content={
          categoryItemData?.menuExist === true
            ? "Please delete the menu items before deleting the category"
            : "You will not be able to recover this category"
        }
        name={categoryItemData?.name ? categoryItemData?.name : null}
        firstBtn={categoryItemData?.menuExist === true ? "Cancel" : "No"}
        secondBtn={
          deleteCategoryMutation.isPending
            ? "Please Wait"
            : categoryItemData?.menuExist === true
            ? "Ok"
            : "Yes ! Delete it"
        }
        btnLoading={deleteCategoryMutation.isPending}
        onCloseConfirmDelete={onCloseConfirmDelete}
        onConfirmDelete={
          categoryItemData?.menuExist === true
            ? onCloseConfirmDelete
            : confirmDeleteMenu
        }
        themeMode={props.themeMode}
      />
      <AlertMsg msgAlert={msgAlert} onCloseAlertMsg={onCloseAlertMsg} />
    </Container>
  );
};

const mapStateToProps = (state) => {
  return {
    drawerOpen: state.userData.drawerOpen,
  };
};

export default connect(mapStateToProps, null)(SettingsMenu);

const styles = {
  settingsText: {
    fontFamily: "GilroyBold",
  },
  titleMainBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  titleViewText: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  titleText: {
    color: "#000000",
    fontSize: 18,
    fontFamily: "GilroySemiBold",
  },
  mainView: {
    backgroundColor: "#FFF",
    borderRadius: 8,
  },
  editCatOrderDiv: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BTN_COLOR,
    borderRadius: "8px",
    cursor: "pointer",
    height: 45,
    width: "fit-content",
    px: 1.5,
    py: 0.8,
  },
  editCatOrderText: {
    color: BTN_TEXT_COLOR,
    fontSize: 14,
    fontFamily: "GilroySemiBold",
    marginLeft: 1,
  },
  addCategoryBtn: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BTN_COLOR,
    borderRadius: "8px",
    cursor: "pointer",
    height: 45,
    width: "fit-content",
    minWidth: 150,
    ml: 1,
    px: 1.5,
    py: 0.8,
  },
  addCategoryText: {
    color: BTN_TEXT_COLOR,
    fontSize: 14,
    fontFamily: "GilroySemiBold",
    marginLeft: 1,
  },
};
